/**
 * Outil « Échantillons » (Boîte à outils, 2nde → 1re).
 *
 * Tirer 100 échantillons de taille n (25, 100, 400, 2 500, 10 000) dans une
 * population de proportion p : chaque taille donne une colonne de 100
 * fréquences, la bande [p − 1/√n ; p + 1/√n] est posée dessus et l'on compte
 * les fréquences qui en sortent. Les colonnes se resserrent quand n grandit.
 * « Décider » : une fréquence observée est-elle compatible avec p ?
 * Logique : `sampling.ts`.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@jeraisonne/ui';
import { Note, PRIMARY_BUTTON, SECONDARY_BUTTON, Segmented, ToggleChip } from '../../ui/controls';
import { frenchSpaces } from '../../ui/format';
import { seededRng } from '../../ui/random';
import { FIGURE_BG_CLASS, HaloText } from '../../ui/svg-parts';
import {
  SAMPLES,
  SITUATIONS,
  SIZES,
  band,
  bandText,
  compatible,
  dec,
  decisionExplanation,
  decisionStatement,
  drawFrequencies,
  insideCount,
  isDecimal,
  int,
  makeSeries,
  observed,
  situationById,
  spread,
  type Decision,
  type Size,
} from './sampling';

const W = 340;
const H = 300;
const LEFT = 34;
const TOP = 12;
const BOTTOM = 46;
const PLOT_H = H - TOP - BOTTOM;
const SLOT = (W - LEFT - 6) / SIZES.length;
const IN = 'var(--color-primary-600)';
const OUT = 'var(--color-error-500)';
const P_LINE = 'var(--color-accent-600)';
const ROUNDS = 5;

const yOf = (f: number) => TOP + (1 - f) * PLOT_H;
/** décalage horizontal d'un point dans sa colonne (fixe, pour que le nuage ne tremble pas) */
const jitter = (i: number) => (((i * 37) % 100) / 100 - 0.5) * SLOT * 0.62;

function Columns({
  p,
  pText,
  columns,
}: {
  p: number;
  pText: string;
  columns: Partial<Record<Size, number[]>>;
}) {
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className={cn(
        'border-ink-100 block w-full max-w-[26rem] select-none rounded-2xl border',
        FIGURE_BG_CLASS,
      )}
      role="img"
      aria-label={`Fréquences des échantillons, une colonne par taille, autour de p = ${pText}.`}
      data-testid="samples-figure"
    >
      {/* graduations des fréquences */}
      {Array.from({ length: 11 }, (_, i) => i / 10).map((f) => (
        <g key={f}>
          <line x1={LEFT} x2={W - 4} y1={yOf(f)} y2={yOf(f)} stroke="var(--color-ink-100)" />
          {f * 10 === Math.round(f * 10) && (f * 10) % 2 === 0 && (
            <text
              x={LEFT - 5}
              y={yOf(f) + 4}
              textAnchor="end"
              fontSize={12}
              fill="var(--color-ink-500)"
            >
              {dec(f)}
            </text>
          )}
        </g>
      ))}
      {SIZES.map((n, s) => {
        const cx = LEFT + SLOT * (s + 0.5);
        const freqs = columns[n];
        const [lo, hi] = band(p, n);
        return (
          <g key={n}>
            <rect
              x={cx - SLOT * 0.42}
              width={SLOT * 0.84}
              y={yOf(hi)}
              height={Math.max(1, yOf(lo) - yOf(hi))}
              fill="var(--color-success-500)"
              opacity={freqs ? 0.22 : 0.08}
              rx={3}
            />
            {/* les points hors de la bande en dernier : jamais cachés sous les autres */}
            {freqs &&
              [false, true].map((out) =>
                freqs.map((f, i) => {
                  const inside = f >= lo - 1e-12 && f <= hi + 1e-12;
                  if (inside === out) return null;
                  return (
                    <circle
                      key={`${out ? 'o' : 'i'}${i}`}
                      cx={cx + jitter(i)}
                      cy={yOf(f)}
                      r={out ? 3 : 2.4}
                      fill={inside ? IN : OUT}
                      stroke={out ? 'var(--fig-bg, #fff)' : 'none'}
                      strokeWidth={1}
                    />
                  );
                }),
              )}
            <text
              x={cx}
              y={H - BOTTOM + 18}
              textAnchor="middle"
              fontSize={12}
              fontWeight={700}
              fill="var(--color-ink-700)"
            >
              {int(n)}
            </text>
            <text
              x={cx}
              y={H - BOTTOM + 34}
              textAnchor="middle"
              fontSize={12}
              fill="var(--color-ink-500)"
            >
              {`±${dec(1 / Math.sqrt(n))}`}
            </text>
          </g>
        );
      })}
      <line
        x1={LEFT}
        x2={W - 4}
        y1={yOf(p)}
        y2={yOf(p)}
        stroke={P_LINE}
        strokeWidth={1.5}
        strokeDasharray="6 4"
      />
      <HaloText
        x={W - 8}
        y={yOf(p) - 5}
        textAnchor="end"
        fontSize={12}
        fontWeight={700}
        fill={P_LINE}
      >
        p
      </HaloText>
    </svg>
  );
}

function Explore({ rng }: { rng: () => number }) {
  const [sid, setSid] = useState(SITUATIONS[0]!.id);
  const s = situationById(sid);
  const [n, setN] = useState<Size>(100);
  const [columns, setColumns] = useState<Partial<Record<Size, number[]>>>({});
  const [last, setLast] = useState<Size | null>(null);

  const draw = () => {
    setColumns((c) => ({ ...c, [n]: drawFrequencies(n, s.p, rng) }));
    setLast(n);
  };
  const drawn = SIZES.filter((m) => columns[m]);
  const lastFreqs = last ? columns[last] : undefined;

  return (
    <div className="space-y-3">
      <div role="group" aria-label="Situation" className="flex flex-wrap gap-1.5">
        {SITUATIONS.map((x) => (
          <ToggleChip
            key={x.id}
            on={x.id === sid}
            onClick={() => {
              setSid(x.id);
              setColumns({});
              setLast(null);
            }}
          >
            {x.label}
          </ToggleChip>
        ))}
      </div>
      <p className="text-ink-700 text-sm">
        {frenchSpaces(
          `${s.pLabel} : p = ${s.pText}${s.pText.includes('/') ? ` ${isDecimal(s.p) ? '=' : '≈'} ${dec(s.p, 3)}` : ''}. Un échantillon de taille n : n ${s.unit} ; on note la fréquence ${s.freqOf}.`,
        )}
      </p>
      <div className="space-y-1">
        <p className="text-ink-500 text-xs font-semibold">Taille n des échantillons</p>
        <Segmented
          ariaLabel="Taille n des échantillons"
          value={String(n)}
          options={SIZES.map((m) => ({ value: String(m), label: int(m) }))}
          onChange={(v) => setN(Number(v) as Size)}
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="button" className={PRIMARY_BUTTON} onClick={draw}>
          {frenchSpaces(`Tirer ${SAMPLES} échantillons de taille ${int(n)}`)}
        </button>
        <button
          type="button"
          className={cn(SECONDARY_BUTTON, 'disabled:opacity-40')}
          onClick={() => {
            setColumns({});
            setLast(null);
          }}
          disabled={drawn.length === 0}
        >
          Effacer
        </button>
      </div>
      <Columns p={s.p} pText={s.pText} columns={columns} />
      <p className="text-ink-500 text-xs">
        {frenchSpaces(
          'Chaque point est la fréquence d’un échantillon ; la bande verte va de p − 1/√n à p + 1/√n. Points rouges : fréquences hors de la bande.',
        )}
      </p>
      <p className="text-ink-800 text-sm" aria-live="polite" data-testid="samples-last">
        {last && lastFreqs
          ? frenchSpaces(
              `Taille ${int(last)} : ${insideCount(lastFreqs, s.p, last)} fréquences sur ${SAMPLES} dans ${bandText(s.p, last)} (bornes comprises) ; elles vont de ${dec(spread(lastFreqs)[0])} à ${dec(spread(lastFreqs)[1])}.`,
            )
          : 'Choisis une taille, puis tire des échantillons.'}
      </p>
      {drawn.length > 0 && (
        <table className="w-full text-left text-sm" data-testid="samples-table">
          <caption className="sr-only">Résumé par taille d’échantillon</caption>
          <thead className="text-ink-500 text-xs">
            <tr>
              <th scope="col" className="py-1 pr-2 font-semibold">
                n
              </th>
              <th scope="col" className="py-1 pr-2 font-semibold">
                p ± 1/√n
              </th>
              <th scope="col" className="py-1 pr-2 font-semibold">
                dans la bande
              </th>
              <th scope="col" className="py-1 font-semibold">
                min – max
              </th>
            </tr>
          </thead>
          <tbody className="text-ink-800">
            {drawn.map((m) => {
              const f = columns[m]!;
              const [a, b] = spread(f);
              return (
                <tr key={m} className="border-ink-100 border-t">
                  <th scope="row" className="py-1 pr-2 font-semibold">
                    {int(m)}
                  </th>
                  <td className="py-1 pr-2">
                    {frenchSpaces(bandText(s.p, m))}
                    {s.p - 1 / Math.sqrt(m) < 0 && (
                      <span className="text-ink-500 block text-xs">borné à 0</span>
                    )}
                  </td>
                  <td className="py-1 pr-2">{`${dec((insideCount(f, s.p, m) * 100) / SAMPLES, 1)} %`}</td>
                  <td className="py-1">{frenchSpaces(`${dec(a)} – ${dec(b)}`)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {drawn.length >= 2 && (
        <Note live={false}>
          {frenchSpaces(
            'Plus n est grand, plus les fréquences se resserrent autour de p : quand n est multiplié par 4, 1/√n est divisé par 2. Et pour chaque taille, la proportion des échantillons dont la fréquence est dans la bande tourne autour de 95 % ou plus : c’est un constat, une série de 100 peut descendre un peu en dessous par hasard.',
          )}
        </Note>
      )}
    </div>
  );
}

/** la bande et la fréquence observée, sur un bout d'axe */
function DecisionStrip({ d }: { d: Decision }) {
  const p = d.situation.p;
  const h = 1 / Math.sqrt(d.n);
  const f = observed(d);
  const lo = Math.max(0, Math.min(p - 3 * h, f - h));
  const hi = Math.min(1, Math.max(p + 3 * h, f + h));
  const SW = 320;
  const xOf = (v: number) => 30 + ((v - lo) / (hi - lo)) * (SW - 60);
  const [a, b] = band(p, d.n);
  const ok = compatible(d);
  const FC = ok ? IN : 'var(--color-error-600)';
  return (
    <svg
      viewBox={`0 0 ${SW} 84`}
      className={cn(
        'border-ink-100 block w-full max-w-[26rem] rounded-2xl border',
        FIGURE_BG_CLASS,
      )}
      role="img"
      aria-label={`Bande ${bandText(p, d.n)} ; fréquence observée ${dec(f)}, ${ok ? 'dans' : 'hors de'} la bande.`}
      data-testid="samples-strip"
    >
      <line x1={10} x2={SW - 10} y1={44} y2={44} stroke="var(--color-ink-400)" />
      <rect
        x={xOf(a)}
        width={xOf(b) - xOf(a)}
        y={34}
        height={20}
        rx={4}
        fill="var(--color-success-500)"
        opacity={0.3}
        stroke="var(--color-success-600)"
      />
      <line x1={xOf(p)} x2={xOf(p)} y1={30} y2={58} stroke={P_LINE} strokeWidth={2} />
      {/* en bas : les bornes et p (séparés d'au moins 1/√n) */}
      <text
        x={xOf(p)}
        y={76}
        textAnchor="middle"
        fontSize={12}
        fontWeight={700}
        fill="var(--color-ink-800)"
      >
        p
      </text>
      <text x={xOf(a)} y={76} textAnchor="middle" fontSize={12} fill="var(--color-ink-700)">
        {dec(a)}
      </text>
      <text x={xOf(b)} y={76} textAnchor="middle" fontSize={12} fill="var(--color-ink-700)">
        {dec(b)}
      </text>
      {/* en haut : f et sa valeur */}
      <circle cx={xOf(f)} cy={44} r={6} fill={FC} stroke="var(--fig-bg, #fff)" strokeWidth={1.5} />
      <text
        x={Math.min(SW - 8, Math.max(8, xOf(f)))}
        y={22}
        textAnchor={xOf(f) < 50 ? 'start' : xOf(f) > SW - 50 ? 'end' : 'middle'}
        fontSize={12}
        fontWeight={700}
        fill={FC}
      >
        {`f = ${dec(f)}`}
      </text>
    </svg>
  );
}

function Decide({ rng }: { rng: () => number }) {
  // une série de 5 : couples (situation, n) différents, 2 ou 3 réponses « douter »
  const [series, setSeries] = useState<Decision[]>(() => makeSeries(rng, ROUNDS));
  const [answer, setAnswer] = useState<boolean | null>(null);
  const [count, setCount] = useState(1);
  const [score, setScore] = useState(0);
  const d = series[count - 1]!;
  const truth = compatible(d);
  const done = answer !== null && count >= ROUNDS;
  const titleRef = useRef<HTMLParagraphElement>(null);
  const moved = useRef(false);
  useEffect(() => {
    if (moved.current) titleRef.current?.focus();
    moved.current = false;
  }, [d]);

  const pick = (v: boolean) => {
    if (answer !== null) return;
    setAnswer(v);
    if (v === truth) setScore(score + 1);
  };
  const next = () => {
    moved.current = true;
    if (done) {
      setSeries(makeSeries(rng, ROUNDS));
      setCount(1);
      setScore(0);
    } else setCount(count + 1);
    setAnswer(null);
  };
  const choice = (v: boolean, label: string) => (
    <button
      type="button"
      onClick={() => pick(v)}
      aria-pressed={answer === v}
      aria-disabled={answer !== null && answer !== v}
      className={cn(
        'min-h-11 flex-1 rounded-xl border px-3 py-2 text-sm font-semibold transition-colors',
        answer === null && 'border-ink-200 text-ink-800 hover:bg-ink-50',
        answer !== null && v === truth && 'border-success-600 bg-success-50 text-ink-900',
        answer === v && v !== truth && 'border-error-500 bg-error-50 text-ink-900',
        answer !== null && answer !== v && v !== truth && 'border-ink-100 text-ink-500',
      )}
    >
      {label}
    </button>
  );

  return (
    <div className="space-y-3">
      <p className="text-ink-500 text-xs font-semibold uppercase tracking-wide">
        Question {count} / {ROUNDS} · {score} juste{score > 1 ? 's' : ''}
      </p>
      <p className="text-ink-600 text-xs">
        {frenchSpaces(
          'Pour aller plus loin que le programme : prendre une décision à partir d’un seul échantillon.',
        )}
      </p>
      <p ref={titleRef} tabIndex={-1} className="text-ink-900 text-base font-semibold outline-none">
        {frenchSpaces(decisionStatement(d))}
      </p>
      <p className="text-ink-700 text-sm">
        {frenchSpaces(
          `Compare la fréquence observée à l’intervalle [p − 1/\u2060√n ; p + 1/\u2060√n] (bornes comprises), avec n = ${int(d.n)}.`,
        )}
      </p>
      <div role="group" aria-label="Ta réponse" className="flex flex-wrap gap-2">
        {choice(true, 'Rien ne permet de douter')}
        {choice(false, 'On peut douter')}
      </div>
      {answer !== null && (
        <>
          <DecisionStrip d={d} />
          <Note live tone={answer === truth ? 'success' : 'error'}>
            {frenchSpaces(`${answer === truth ? 'Exact. ' : 'Non. '}${decisionExplanation(d)}`)}
          </Note>
          <div className="flex flex-wrap items-center gap-3">
            {done && (
              <p className="text-ink-900 text-sm font-semibold">{`Score : ${score} / ${ROUNDS}`}</p>
            )}
            <button
              type="button"
              className={done ? SECONDARY_BUTTON : PRIMARY_BUTTON}
              onClick={next}
            >
              {done ? 'Recommencer' : 'Question suivante'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export function SamplesTool({ seed }: { /** graine des tirages (tests) */ seed?: number }) {
  const rng = useMemo(() => (seed === undefined ? Math.random : seededRng(seed)), [seed]);
  const [mode, setMode] = useState<'explore' | 'decide'>('explore');
  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Ce qu’on fait"
        value={mode}
        options={[
          { value: 'explore', label: 'Échantillonner' },
          { value: 'decide', label: 'Décider (plus loin)' },
        ]}
        onChange={setMode}
      />
      <div hidden={mode !== 'explore'}>
        <Explore rng={rng} />
      </div>
      <div hidden={mode !== 'decide'}>
        <Decide rng={rng} />
      </div>
    </div>
  );
}
